import { useState, useEffect } from 'react';
import { getPreferences, updatePreferences } from '../services/dashboard';
import { getBillingStatus, redirectToPortal } from '../services/stripe';
import { useAuth } from '../context/AuthContext';
import './Settings.css';

const ROLE_OPTIONS = ['Frontend', 'Backend', 'Full Stack', 'Mobile', 'DevOps', 'Data Science', 'ML / AI', 'Product', 'Design'];
const COMPANY_SIZE_OPTIONS = ['Startup (1-50)', 'Mid-size (51-500)', 'Large (500-5000)', 'Enterprise (5000+)'];
const FRESHNESS_OPTIONS = [
  { label: '24h', value: '24h' },
  { label: '48h', value: '48h' },
  { label: '7d', value: '7d' },
];
const VOLUME_OPTIONS = [
  { label: 'Low', value: 'low' },
  { label: 'Medium', value: 'medium' },
  { label: 'High', value: 'high' },
];

export default function Settings() {
  const { user } = useAuth();

  // Loading & messages
  const [loading, setLoading] = useState(true);
  const [profileMsg, setProfileMsg] = useState(null);
  const [prefsMsg, setPrefsMsg] = useState(null);

  // Profile
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');

  // Preferences
  const [roles, setRoles] = useState([]);
  const [remoteEnabled, setRemoteEnabled] = useState(false);
  const [locations, setLocations] = useState([]);
  const [locationInput, setLocationInput] = useState('');
  const [minPay, setMinPay] = useState('');
  const [companySize, setCompanySize] = useState([]);
  const [freshness, setFreshness] = useState('48h');
  const [coldEmail, setColdEmail] = useState(false);
  const [dailyVolume, setDailyVolume] = useState('medium');

  // Billing
  const [billing, setBilling] = useState(null);

  // Connected accounts
  const [gmailConnected, setGmailConnected] = useState(false);
  const [gmailEmail, setGmailEmail] = useState('');
  const [outlookConnected, setOutlookConnected] = useState(false);
  const [outlookEmail, setOutlookEmail] = useState('');

  // Delete confirmation
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setEmail(user.email || '');
    }
  }, [user]);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [prefs, bill] = await Promise.allSettled([
          getPreferences(),
          getBillingStatus(),
        ]);

        if (prefs.status === 'fulfilled' && prefs.value) {
          const p = prefs.value;
          setRoles(p.roles || []);
          setRemoteEnabled(!!p.remote);
          setLocations(p.locations || []);
          setMinPay(p.min_pay ?? '');
          setCompanySize(p.company_size || []);
          setFreshness(p.listing_freshness || '48h');
          setColdEmail(!!p.cold_email_enabled);
          setDailyVolume(p.daily_volume || 'medium');
        }

        if (bill.status === 'fulfilled' && bill.value) {
          setBilling(bill.value);
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setProfileMsg({ type: 'success', text: 'Profile saved successfully.' });
    setTimeout(() => setProfileMsg(null), 3000);
  };

  const handleSavePreferences = async (e) => {
    e.preventDefault();
    setPrefsMsg(null);
    try {
      await updatePreferences({
        roles,
        locations,
        min_pay: Number(minPay) || 0,
        remote: remoteEnabled,
        cold_email_enabled: coldEmail,
        daily_volume: dailyVolume,
        listing_freshness: freshness,
        company_size: companySize,
      });
      setPrefsMsg({ type: 'success', text: 'Preferences saved successfully.' });
    } catch (err) {
      setPrefsMsg({ type: 'error', text: 'Failed to save preferences. Please try again.' });
    }
    setTimeout(() => setPrefsMsg(null), 3000);
  };

  const addLocation = () => {
    const trimmed = locationInput.trim();
    if (trimmed && !locations.includes(trimmed)) {
      setLocations([...locations, trimmed]);
    }
    setLocationInput('');
  };

  const removeLocation = (loc) => {
    setLocations(locations.filter(l => l !== loc));
  };

  const toggleRole = (role) => {
    setRoles(prev => prev.includes(role) ? prev.filter(r => r !== role) : [...prev, role]);
  };

  const toggleCompanySize = (size) => {
    setCompanySize(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
  };

  const handleDeleteAccount = () => {
    setShowDeleteConfirm(true);
  };

  const confirmDelete = () => {
    setShowDeleteConfirm(false);
    alert('Account deletion request submitted. Our team will process this within 48 hours.');
  };

  if (loading) {
    return (
      <div className="settings-page">
        <div className="settings-container">
          <div className="settings-card">
            <div className="settings-loading">
              <div className="settings-spinner" />
              <p>Loading settings...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const planLabel = billing?.plan || 'Free';
  const isPaid = planLabel.toLowerCase() !== 'free';

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-card">
          <h1 className="settings-title">Settings</h1>

          {/* ── SECTION 1: Profile ── */}
          <section className="settings-section">
            <h2 className="settings-section-heading">Profile</h2>
            <form onSubmit={handleSaveProfile}>
              <div className="settings-field">
                <label className="settings-label">Name</label>
                <input
                  className="settings-input"
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div className="settings-field">
                <label className="settings-label">Email</label>
                <input
                  className="settings-input"
                  type="email"
                  value={email}
                  disabled
                  readOnly
                />
              </div>
              {profileMsg && (
                <div className={`settings-msg settings-msg--${profileMsg.type}`}>{profileMsg.text}</div>
              )}
              <button type="submit" className="settings-btn settings-btn--primary">Save Profile</button>
            </form>
          </section>

          {/* ── SECTION 2: Job Search Preferences ── */}
          <section className="settings-section">
            <h2 className="settings-section-heading">Job Search Preferences</h2>
            <form onSubmit={handleSavePreferences}>

              {/* Role types */}
              <div className="settings-field">
                <label className="settings-label">Role Types</label>
                <div className="settings-checkbox-group">
                  {ROLE_OPTIONS.map(role => (
                    <label key={role} className={`settings-checkbox-item${roles.includes(role) ? ' is-checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={roles.includes(role)}
                        onChange={() => toggleRole(role)}
                      />
                      <span>{role}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Location */}
              <div className="settings-field">
                <label className="settings-label">Location</label>
                <div className="settings-toggle-row">
                  <span>Include remote positions</span>
                  <button
                    type="button"
                    className={`settings-toggle${remoteEnabled ? ' is-on' : ''}`}
                    onClick={() => setRemoteEnabled(!remoteEnabled)}
                    aria-label="Toggle remote"
                  >
                    <span className="settings-toggle-knob" />
                  </button>
                </div>
                <div className="settings-location-input-row">
                  <input
                    className="settings-input"
                    type="text"
                    value={locationInput}
                    onChange={e => setLocationInput(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addLocation(); } }}
                    placeholder="Add a city or region"
                  />
                  <button type="button" className="settings-btn settings-btn--secondary" onClick={addLocation}>Add</button>
                </div>
                {locations.length > 0 && (
                  <div className="settings-tags">
                    {locations.map((loc, i) => (
                      <span key={i} className="settings-tag">
                        {loc}
                        <button type="button" className="settings-tag-remove" onClick={() => removeLocation(loc)}>&times;</button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Minimum hourly rate */}
              <div className="settings-field">
                <label className="settings-label">Minimum Hourly Rate</label>
                <div className="settings-dollar-input">
                  <span className="settings-dollar-prefix">$</span>
                  <input
                    className="settings-input settings-input--dollar"
                    type="number"
                    min="0"
                    value={minPay}
                    onChange={e => setMinPay(e.target.value)}
                    placeholder="0"
                  />
                </div>
              </div>

              {/* Company size */}
              <div className="settings-field">
                <label className="settings-label">Company Size</label>
                <div className="settings-checkbox-group">
                  {COMPANY_SIZE_OPTIONS.map(size => (
                    <label key={size} className={`settings-checkbox-item${companySize.includes(size) ? ' is-checked' : ''}`}>
                      <input
                        type="checkbox"
                        checked={companySize.includes(size)}
                        onChange={() => toggleCompanySize(size)}
                      />
                      <span>{size}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Listing freshness */}
              <div className="settings-field">
                <label className="settings-label">Listing Freshness</label>
                <div className="settings-segmented">
                  {FRESHNESS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`settings-seg-btn${freshness === opt.value ? ' is-active' : ''}`}
                      onClick={() => setFreshness(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Cold email outreach */}
              <div className="settings-field">
                <div className="settings-toggle-row">
                  <span>Enable cold email outreach</span>
                  <button
                    type="button"
                    className={`settings-toggle${coldEmail ? ' is-on' : ''}`}
                    onClick={() => setColdEmail(!coldEmail)}
                    aria-label="Toggle cold emails"
                  >
                    <span className="settings-toggle-knob" />
                  </button>
                </div>
              </div>

              {/* Daily volume */}
              <div className="settings-field">
                <label className="settings-label">Daily Volume</label>
                <div className="settings-segmented">
                  {VOLUME_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`settings-seg-btn${dailyVolume === opt.value ? ' is-active' : ''}`}
                      onClick={() => setDailyVolume(opt.value)}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {prefsMsg && (
                <div className={`settings-msg settings-msg--${prefsMsg.type}`}>{prefsMsg.text}</div>
              )}
              <button type="submit" className="settings-btn settings-btn--primary">Save Preferences</button>
            </form>
          </section>

          {/* ── SECTION 3: Billing ── */}
          <section className="settings-section">
            <h2 className="settings-section-heading">Billing</h2>
            <div className="settings-billing-row">
              <span className="settings-label">Current Plan</span>
              <span className={`settings-plan-badge settings-plan-badge--${planLabel.toLowerCase()}`}>{planLabel}</span>
            </div>
            {isPaid ? (
              <button className="settings-btn settings-btn--secondary" onClick={redirectToPortal}>
                Manage Subscription
              </button>
            ) : (
              <a href="/pricing" className="settings-btn settings-btn--primary settings-btn--inline">
                Upgrade
              </a>
            )}
          </section>

          {/* ── SECTION 4: Connected Accounts ── */}
          <section className="settings-section">
            <h2 className="settings-section-heading">Connected Accounts</h2>
            <div className="settings-connected-row">
              <div className="settings-connected-label">
                <span className="settings-connected-icon">G</span>
                <span>Gmail</span>
              </div>
              {gmailConnected ? (
                <span className="settings-connected-email">{gmailEmail}</span>
              ) : (
                <button
                  className="settings-btn settings-btn--secondary"
                  onClick={() => { setGmailConnected(true); setGmailEmail('connected@gmail.com'); }}
                >
                  Connect Gmail
                </button>
              )}
            </div>
            <div className="settings-connected-row">
              <div className="settings-connected-label">
                <span className="settings-connected-icon settings-connected-icon--outlook">O</span>
                <span>Outlook</span>
              </div>
              {outlookConnected ? (
                <span className="settings-connected-email">{outlookEmail}</span>
              ) : (
                <button
                  className="settings-btn settings-btn--secondary"
                  onClick={() => { setOutlookConnected(true); setOutlookEmail('connected@outlook.com'); }}
                >
                  Connect Outlook
                </button>
              )}
            </div>
          </section>

          {/* ── SECTION 5: Danger Zone ── */}
          <section className="settings-section settings-section--danger">
            <h2 className="settings-section-heading settings-section-heading--danger">Danger Zone</h2>
            <p className="settings-danger-desc">
              Permanently delete your account and all associated data. This action cannot be undone.
            </p>
            <button className="settings-btn settings-btn--danger" onClick={handleDeleteAccount}>
              Delete Account
            </button>

            {showDeleteConfirm && (
              <div className="settings-confirm-overlay">
                <div className="settings-confirm-dialog">
                  <h3>Are you sure?</h3>
                  <p>This will permanently delete your account, preferences, applications, and all data. This cannot be undone.</p>
                  <div className="settings-confirm-actions">
                    <button className="settings-btn settings-btn--secondary" onClick={() => setShowDeleteConfirm(false)}>
                      Cancel
                    </button>
                    <button className="settings-btn settings-btn--danger" onClick={confirmDelete}>
                      Yes, Delete My Account
                    </button>
                  </div>
                </div>
              </div>
            )}
          </section>

        </div>
      </div>
    </div>
  );
}
